<template>
    <form-wrapper @submit="handleFormSubmission(data)">
        <form-row>
            <form-cell :width="columnWidth">
                <form-field :label="$t('forms.auth.schedule.fields.location.label')">
                    <select-input
                        class="schedule-apply__input"
                        :placeholder="$t('forms.auth.schedule.fields.location.placeholder')"
                        :hasAutofocus="true"
                        :options="locations"
                        :value="currentLocationId"
                        @change="changeCurrentLocationId($event)"
                    ></select-input>
                </form-field>
            </form-cell>

            <form-cell v-if="!showReview" class="appointment_length--row" :isDisabled="!currentLocationId" :width="columnWidth">
                <select-field
                    class="schedule-apply__input"
                    :label="$t('forms.auth.schedule.fields.appointment_length.label')"
                    :placeholder="$t('forms.auth.schedule.fields.appointment_length.placeholder')"
                    :hint="$t('forms.auth.schedule.fields.appointment_length.hint')"
                    :options="appointmentLengthOptions"
                    :formData="locationSchedule(currentLocationId)"
                    valuePath="appointmentLength"
                    @change="changeLocationSchedule($event, currentLocationId)"
                ></select-field>
            </form-cell>
            <form-cell v-if="!showReview" :isDisabled="!currentLocationId" :width="columnWidth">
                <date-range-field
                    :formData="data"
                    :label="$t('forms.auth.schedule.fields.start_date.label')"
                    openSide="right"
                    :isSingle="true"
                    iconCustomClass="availability-schedule-form-icon"
                    valuePath="startWeekDate"
                    @change="changeStartDate($event)"
                ></date-range-field>
            </form-cell>
        </form-row>

        <form-row v-if="!showReview">
            <form-cell :isDisabled="isFormDisabled" style="max-width: 100%">
                <div id="schedule" class="schedule">
                    <div class="schedule__container">
                        <div class="schedule__days">
                            <form-field :label="scheduleDaysLabel">
                                <schedule-days
                                    :currentDay="currentDay"
                                    :filledDays="filledDays(currentLocationId)"
                                    @click="changeCurrentDay($event)"
                                ></schedule-days>
                            </form-field>
                        </div>
                        <div class="schedule__frequency">
                            <radio-field
                                :label="$t('forms.auth.schedule.fields.frequency.label')"
                                :isInline="false"
                                :options="frequencyOptions"
                                :formData="locationSchedule(currentLocationId)"
                                valuePath="frequency"
                                @change="changeAndApplyLocationSchedule($event, currentLocationId)"
                            ></radio-field>
                        </div>
                    </div>

                    <div class="schedule__timeline">
                        <schedule-timeline
                            :dayFrom="dayFrom"
                            :dayTo="dayTo"
                            :appointmentLength="currentAppointmentLength"
                            :formData="locationSchedule(currentLocationId)"
                            :valuePath="slotsForDayPath(currentDay)"
                            @change="changeLocationSchedule($event, currentLocationId)"
                        ></schedule-timeline>
                    </div>
                </div>
            </form-cell>
        </form-row>

        <form-row v-if="showReview">
            <form-cell :isDisabled="isFormDisabled">
                <schedule-summary
                    :locations="locations"
                    :schedule="data.appliedSchedule"
                    :showDiscardButton="true"
                    @discard="discardSchedule($event)"
                ></schedule-summary>
            </form-cell>
        </form-row>

        <form-row v-if="showReview">
            <form-cell>
                <div class="schedule__start-date">
                    <form-field :label="$t('forms.auth.schedule.fields.start_date.label')" :isInline="true">
                        <schedule-start-date :frequency="currentFrequency" :date="newStartDate"></schedule-start-date>
                    </form-field>
                </div>
            </form-cell>
        </form-row>

        <template #footer>
            <common-buttons :isDisabled="isFormDisabled">
                <common-button
                    :isSubmittable="false"
                    size="large"
                    :label="$t('forms.auth.schedule.microcopy.back_to_calendar')"
                    type="secondary"
                    @click="emitEvent({ name: 'modify-availability' })"
                ></common-button>
                <common-button
                    :label="
                        showReview
                            ? $t('forms.auth.schedule.microcopy.edit_timeline')
                            : $t('forms.auth.schedule.microcopy.review')
                    "
                    :isSubmittable="false"
                    size="large"
                    type="primary"
                    @click="previewChanges()"
                ></common-button>
                <common-button
                    v-show="showSave && showReview"
                    size="large"
                    :isProcessing="isSending"
                    :label="$t('forms.auth.schedule.microcopy.save')"
                    type="primary"
                ></common-button>
            </common-buttons>
        </template>

        <change-appointment-length-modal
            v-if="isAppointmentLengthModalOpen"
            :scheduleInfo="getLocationNameAndAppointmentLength"
            @apply-time-change="applyTimelineChange()"
            @close="closeAppointmentLengthModal()"
        ></change-appointment-length-modal>

        <reschedule-modal
            v-if="isRescheduleModalOpen"
            :numberOfAppointments="numberOfSlotsToReschedule"
            @close="closeRescheduleModal()"
            @reschedule="reschedule()"
        ></reschedule-modal>

        <availability-changes-modal
            v-if="isAvailabilityChangesModalOpen"
            :locations="locations"
            :schedule="data.appliedSchedule"
            :currentLocationId="currentLocationId"
            @discard-changes="changeLocation(newLocationId)"
            @apply-changes="handleFormSubmission(data)"
            @close="closeAvailabilityChangesModal()"
        ></availability-changes-modal>

        <common-modal
            v-if="isCannotRemoveAllSlotsModalOpen"
            @emitEvent="emitEvent($event, ['close'])"
            @close="closeOneSlotLeftWarningModal()"
        >
            <div class="notification_message_container">
                <span class="notification_message">{{ $t('modals.cannot_remove_all.message') }}</span>
            </div>
            <common-buttons>
                <common-button
                    :label="$t('modals.cannot_remove_all.button')"
                    :isSubmittable="false"
                    type="primary"
                    @click="closeOneSlotLeftWarningModal()"
                ></common-button>
            </common-buttons>
        </common-modal>
    </form-wrapper>
</template>

<script>
    import Store from '~/store'
    import isEqual from 'lodash.isequal'
    import dayjs from 'dayjs'
    import { importComponents } from '~/helpers'
    import UsersAPI from 'api/UsersAPI'
    import PracticesAPI from 'api/PracticesAPI'
    import ScheduleMixin from 'mixins/ScheduleMixin'
    import AdminRegistersPhysicianMixin from 'mixins/AdminRegistersPhysicianMixin'
    import ChangeAppointmentLengthModal from 'components/page-elements/availability/ModifyAvailabilityAppointmentLengthModal'
    import AvailabilityChangesModal from 'components/page-elements/availability/ApplyAvailabilityChangesModal'
    import RescheduleModal from 'components/page-elements/availability/AvailabilityRescheduleModal'
    import { updateStoreMembers } from '~/utils'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import ReferralPathTypeEnum from '@/enums/ReferralPathTypeEnum'

    export default {
        mixins: [ScheduleMixin, AdminRegistersPhysicianMixin, ScreenSizeDetectionMixin],

        components: {
            ...importComponents(require.context('components/forms/auth-schedule-form')),
            ChangeAppointmentLengthModal,
            AvailabilityChangesModal,
            RescheduleModal,
        },

        props: {
            dayFrom: { type: Number, required: true },
            dayTo: { type: Number, required: true },
        },

        data() {
            return {
                isCannotRemoveAllSlotsModalOpen: false,
                isAppointmentLengthModalOpen: false,
                isAvailabilityChangesModalOpen: false,
                isRescheduleModalOpen: false,
                numberOfSlotsToReschedule: 0,
                newLocationId: null,
                currentLocationId: '0',
                currentDay: 0,
                hasUnappliedSlots: false,
                data: this.createEmptySchedule(),
                showReview: false,
                newStartDate: null,
            }
        },

        computed: {
            frequencyOptions() {
                return ['weekly', 'biweekly'].map((value) => ({
                    label: this.$t(`forms.auth.schedule.fields.frequency.options.${value}`),
                    value,
                }))
            },

            appointmentLengthOptions() {
                return [15, 30, 60].map((value) => ({
                    label: this.$tc('forms.auth.schedule.fields.appointment_length.minutes', value),
                    value,
                }))
            },

            columnWidth() {
                return this.isTabletSmall ? '' : 'fifth'
            },

            isFormDisabled() {
                return !this.currentLocationId || !this.locationSchedule(this.currentLocationId).appointmentLength
            },

            currentAppointmentLength() {
                return this.locationSchedule(this.currentLocationId).appointmentLength || 15
            },

            currentFrequency() {
                return this.locationSchedule(this.currentLocationId).frequency || 'weekly'
            },

            showSave() {
                const hasNoSlots =
                    this.$get(this.locationSchedule(this.currentLocationId), 'days').filter((daySchedule) => daySchedule.length)
                        .length === 0
                const {
                    days: loadedDays,
                    frequency: loadedFrequency,
                    appointmentLength: loadedAppointmentLength,
                } = this.data.loadedSchedule[this.currentLocationId]
                const {
                    days: appliedDays,
                    frequency: appliedFrequency,
                    appointmentLength: appliedAppointmentLength,
                } = this.data.appliedSchedule[this.currentLocationId]
                const { days: emptyDays } = this.data.emptySchedule[this.currentLocationId]

                const isFrequencyChanged = appliedFrequency !== loadedFrequency
                const isAppointmentLengthChanged = appliedAppointmentLength !== loadedAppointmentLength

                return (
                    !hasNoSlots &&
                    !isEqual(appliedDays, emptyDays) &&
                    (!isEqual(appliedDays, loadedDays) || isFrequencyChanged || isAppointmentLengthChanged || this.newStartDate)
                )
            },

            isScheduleActionsDisabled() {
                const dayAppliedSchedule = this.getDayFromSchedule('appliedSchedule')
                const dayDraftSchedule = this.getDayFromSchedule('draftSchedule')
                let isRepeatChanged = false
                if (
                    this.data.appliedSchedule[this.currentLocationId] &&
                    this.data.draftSchedule[this.currentLocationId] &&
                    this.data.appliedSchedule[this.currentLocationId].frequency !==
                        this.data.draftSchedule[this.currentLocationId].frequency
                ) {
                    isRepeatChanged = true
                }
                return isEqual(dayAppliedSchedule, dayDraftSchedule) && !isRepeatChanged
            },

            getLocationNameAndAppointmentLength() {
                return {
                    name: this.locations.find((location) => location.value === this.currentLocationId).label,
                    appointmentLength: this.data.draftSchedule[this.currentLocationId].appointmentLength,
                }
            },

            scheduleDaysLabel() {
                const daysWithAvailability = []

                this.data.loadedSchedule[this.currentLocationId].days.forEach((day, index) => {
                    if (day.length) {
                        daysWithAvailability.push(this.dayLabel(index))
                    }
                })

                if (daysWithAvailability.length) {
                    return this.$t('forms.auth.schedule.fields.days.schedule_set_label', {
                        days: daysWithAvailability.join(', '),
                    })
                }
                return this.$t('forms.auth.schedule.fields.days.label')
            },
        },

        methods: {
            previewChanges() {
                this.showReview = !this.showReview
                this.applyTemporaryScheduleChanges()
            },

            dayLabel(index) {
                return dayjs()
                    .day(index + 1)
                    .format('ddd')
            },

            createEmptySchedule() {
                return {
                    appliedSchedule: this.createInitialSchedule(),
                    draftSchedule: this.createInitialSchedule(),
                    loadedSchedule: this.createInitialSchedule(),
                    emptySchedule: this.createInitialSchedule(),
                }
            },
            applyTemporaryScheduleChanges() {
                if (
                    this.$get(this.locationSchedule(this.currentLocationId), 'days').filter((daySchedule) => daySchedule.length)
                        .length === 0
                ) {
                    this.isCannotRemoveAllSlotsModalOpen = true
                } else {
                    this.applySchedule({
                        locationId: this.currentLocationId,
                        days: [this.currentDay],
                        slots: this.locationSlots(this.currentLocationId, this.currentDay),
                    })
                }
            },

            closeOneSlotLeftWarningModal() {
                this.discardDraft(this.currentLocationId, this.currentDay)
                this.isCannotRemoveAllSlotsModalOpen = false
            },

            getDayFromSchedule(scheduleType) {
                return this.data[scheduleType][Number(this.currentLocationId) || 0].days[this.currentDay]
            },

            closeAppointmentLengthModal() {
                this.data.draftSchedule = JSON.parse(JSON.stringify(this.data.loadedSchedule))
                this.data.appliedSchedule = JSON.parse(JSON.stringify(this.data.loadedSchedule))
                this.isAppointmentLengthModalOpen = false
            },

            closeAvailabilityChangesModal() {
                this.isAvailabilityChangesModalOpen = false
            },

            closeRescheduleModal() {
                this.isRescheduleModalOpen = false
            },

            createInitialSchedule() {
                return this.locations.reduce((schedule, location) => {
                    schedule[location.value] = {
                        appointmentLength: undefined,
                        frequency: 'weekly',
                        days: Array(7).fill([]),
                    }
                    return schedule
                }, {})
            },

            changeCurrentLocationId(newLocationId) {
                if (
                    !isEqual(
                        this.data.loadedSchedule[this.currentLocationId],
                        this.data.appliedSchedule[this.currentLocationId],
                    ) &&
                    !isEqual(
                        this.data.appliedSchedule[this.currentLocationId].days,
                        this.data.emptySchedule[this.currentLocationId].days,
                    )
                ) {
                    this.newLocationId = newLocationId
                    this.isAvailabilityChangesModalOpen = true
                } else {
                    this.changeLocation(newLocationId)
                }
            },

            changeLocation(newLocationId) {
                this.currentLocationId = newLocationId
                this.data = this.createEmptySchedule()
                this.getCurrentAvailability(this.$auth.getActiveMember().id, this.$auth.getPractice().id, newLocationId)
                this.isAvailabilityChangesModalOpen = false

                this.goToLocation(newLocationId)
            },

            goToLocation(locationId) {
                locationId = Number(locationId)
                const currentQueryLocationId = Number(this.$route.query.locationId || 0)
                if (locationId === currentQueryLocationId) return
                const selectedLocation = this.$auth.getUserLocations().find((loc) => Number(loc.id) === locationId)
                if (selectedLocation && selectedLocation.isAvailabilitySet) {
                    const query = Object.assign({}, this.$route.query)
                    if (Number(locationId) === 0) {
                        delete query.locationId
                    } else {
                        query.locationId = locationId.toString()
                    }
                    this.$router.replace({ query })
                }
            },

            changeCurrentDay(newCurrentDay) {
                if (this.hasUnappliedSlots) {
                    if (
                        this.$get(this.locationSchedule(this.currentLocationId), 'days').filter(
                            (daySchedule) => daySchedule.length,
                        ).length === 0
                    ) {
                        this.isCannotRemoveAllSlotsModalOpen = true
                    } else {
                        this.applyTemporaryScheduleChanges()
                        this.hasUnappliedSlots = false
                        this.currentDay = newCurrentDay
                    }
                } else {
                    this.currentDay = newCurrentDay
                }
            },

            discardSchedule({ locationId, days }) {
                days.forEach((day) => {
                    this.discardDay(locationId, day)
                    this.discardDay(locationId, day, true)
                })
            },

            applySchedule({ locationId, days, slots: value }) {
                days.forEach((day) => {
                    const data = { path: this.slotsForDayPath(day), value }
                    this.changeLocationSchedule(data, locationId)
                    this.changeLocationSchedule(data, locationId, true)
                })

                this.changeLocationSchedule(
                    {
                        path: 'appointmentLength',
                        value: this.locationSchedule(locationId).appointmentLength,
                    },
                    locationId,
                    true,
                )
                this.changeLocationSchedule(
                    {
                        path: 'frequency',
                        value: this.locationSchedule(locationId).frequency,
                    },
                    locationId,
                    true,
                )

                this.hasUnappliedSlots = false
            },

            getCurrentAvailability(userId, practiceId, locationId) {
                UsersAPI.getAvailability(userId, practiceId, locationId).then(
                    (res) => {
                        if (!res?.slots?.length) {
                            return
                        }

                        const schedule = {
                            id: res.id,
                            appointmentLength: res.appointmentLength,
                            frequency: res.repeat,
                            days: res.slots,
                            startWeekDate: res.startWeekDate,
                        }
                        this.data.draftSchedule[locationId] = JSON.parse(JSON.stringify(schedule))
                        this.data.appliedSchedule[locationId] = JSON.parse(JSON.stringify(schedule))
                        this.data.loadedSchedule[locationId] = JSON.parse(JSON.stringify(schedule))

                        this.data.startWeekDate = {
                            startDate: new Date(res.startWeekDate) || new Date(),
                        }

                        this.changeStartDate({
                            path: 'startWeekDate',
                            value: { startDate: this.data.startWeekDate.startDate, endDate: this.data.startWeekDate.startDate },
                        })

                        this.currentDay = schedule.days.findIndex((day) => day.length)
                    },
                    () => {
                        this.currentDay = 0
                    },
                )
            },

            applyTimelineChange() {
                this.isAppointmentLengthModalOpen = false
                this.data.appliedSchedule = JSON.parse(JSON.stringify(this.data.draftSchedule))
            },

            calculateFromTimeShift(time, appointmentLength) {
                if (time % appointmentLength) {
                    return {
                        value: time - (time % appointmentLength),
                        oldValue: time,
                    }
                }
                return {
                    value: time,
                    oldValue: time,
                }
            },

            calculateToTimeShift(time, appointmentLength) {
                if (time % appointmentLength) {
                    return {
                        value: time + appointmentLength - (time % appointmentLength),
                        oldValue: time,
                    }
                }
                return {
                    value: time,
                    oldValue: time,
                }
            },

            calculateTimeShift(days, appointmentLength) {
                let shiftedDays = []
                days.forEach((day) => {
                    let from
                    let to
                    let toOldValue
                    let fromOldValue
                    const calculatedDay = []
                    day.sort((a, b) => a.from - b.from).forEach((slot, index) => {
                        const { value: fromTime, oldValue: fOldValue } = this.calculateFromTimeShift(slot.from, appointmentLength)
                        const { value: toTime, oldValue: tOldValue } = this.calculateToTimeShift(slot.to, appointmentLength)

                        if (!from || !to) {
                            from = fromTime
                            fromOldValue = fOldValue
                        } else if (to < fromTime) {
                            calculatedDay.push({
                                from,
                                to,
                                isFromChanged: fromOldValue !== from,
                                isToChanged: toOldValue !== to,
                                fromOldValue,
                                toOldValue,
                            })
                            from = fromTime
                            fromOldValue = fOldValue
                        }
                        to = toTime
                        toOldValue = tOldValue

                        if (index === day.length - 1) {
                            calculatedDay.push({
                                from,
                                to,
                                isFromChanged: fromOldValue !== from,
                                isToChanged: toOldValue !== to,
                                fromOldValue,
                                toOldValue,
                            })
                        }
                    })

                    shiftedDays.push(calculatedDay)
                })
                return shiftedDays
            },

            addNewAvailabilityLocation(userId, practiceId, appliedSchedule) {
                const request = UsersAPI.setSchedule(userId, practiceId, appliedSchedule)

                return this.handleRequest(request).then((res) => {
                    this.changeLocation(res[0].practiceLocationId || '0')
                    this.isSending = false
                    return PracticesAPI.getUserLocations(userId).then((locations) =>
                        Store.dispatch('auth/setUserLocations', locations),
                    )
                })
            },
            reschedule() {
                this.modifyAvailability(true)
            },

            redirectToReferrals() {
                this.$router.push({
                    name: `referrals.received.referrals`,
                    params: {
                        type: this.type === ReferralPathTypeEnum.RECEIVED,
                    },
                    query: {
                        status: 'RESCHEDULE_AVAILABILITY_CHANGE',
                    },
                })
            },

            modifyExistingAvailabilityLocation(appliedSchedule, reschedule) {
                const { id: userId } = this.$auth.getActiveMember()
                const { id: practiceId } = this.$auth.getPractice()

                const request = UsersAPI.modifySchedule(userId, practiceId, appliedSchedule)
                this.handleRequest(request)
                    .then(() => {
                        this.data.loadedSchedule[this.currentLocationId] = JSON.parse(
                            JSON.stringify(this.data.appliedSchedule[this.currentLocationId]),
                        )
                        this.isSending = false
                        this.isRescheduleModalOpen = false
                        this.emitEvent({ name: 'availability-changed' })
                    })
                    .then(updateStoreMembers)
                    .then(() => {
                        if (reschedule) {
                            this.redirectToReferrals()
                        }
                    })
            },

            modifyAvailability(reschedule) {
                let req = {}
                req[this.currentLocationId] = this.data.appliedSchedule[this.currentLocationId]
                this.modifyExistingAvailabilityLocation(req, reschedule)
            },

            handleFormSubmission({ appliedSchedule }) {
                this.isAvailabilityChangesModalOpen = false
                const { id: userId } = this.$auth.getActiveMember()
                const { id: practiceId } = this.$auth.getPractice()

                let updatePromise
                if (isEqual(this.data.loadedSchedule[this.currentLocationId], this.data.emptySchedule[this.currentLocationId])) {
                    this.isAvailabilityChangesModalOpen = false
                    let req = {}
                    req[this.currentLocationId] = appliedSchedule[this.currentLocationId]
                    updatePromise = this.addNewAvailabilityLocation(userId, practiceId, req)
                } else {
                    const appliedLocationSchedule = {
                        [this.currentLocationId]: appliedSchedule[this.currentLocationId],
                    }

                    updatePromise = UsersAPI.countSlotsForReschedule(userId, practiceId, appliedLocationSchedule).then((res) => {
                        const resNumberOfSlotsToReschedule = res[0].count
                        if (resNumberOfSlotsToReschedule > 0) {
                            this.numberOfSlotsToReschedule = resNumberOfSlotsToReschedule
                            this.isRescheduleModalOpen = true
                        } else {
                            this.modifyAvailability()
                        }
                    })
                }
                updatePromise.then(() => this.goToLocation(this.currentLocationId))
                this.$auth.forceUpdatePractice()
            },

            changeStartDate(event) {
                this.change(event)
                this.newStartDate = event.value.startDate
                this.data.appliedSchedule[this.currentLocationId].startWeekDate = dayjs(this.newStartDate).format('YYYY-MM-DD')
            },
        },

        watch: {
            $route(to) {
                this.currentLocationId = Number(to.query.locationId) || '0'
                this.changeLocation(this.currentLocationId)
            },
        },

        mounted() {
            if (this.$route.query.locationId) {
                this.currentLocationId =
                    this.$route.query.locationId === '0' ? this.$route.query.locationId : Number(this.$route.query.locationId)
                this.changeCurrentLocationId(this.currentLocationId)
            }

            if (this.$route.query.showReview) {
                this.showReview = false
            }

            this.getCurrentAvailability(this.$auth.getActiveMember().id, this.$auth.getPractice().id, this.currentLocationId)

            this.$on('local:afterChange:draftSchedule.*.appointmentLength', () => {
                let draftSchedule = this.locationSchedule(this.currentLocationId)
                const loadedSchedule = this.data.loadedSchedule[this.currentLocationId]
                const emptySchedule = this.data.emptySchedule[this.currentLocationId]

                if (loadedSchedule.appointmentLength !== draftSchedule.appointmentLength) {
                    draftSchedule.days = this.calculateTimeShift(loadedSchedule.days, draftSchedule.appointmentLength)
                    if (!isEqual(loadedSchedule, emptySchedule)) {
                        this.isAppointmentLengthModalOpen = true
                    } else {
                        this.data.appliedSchedule = JSON.parse(JSON.stringify(this.data.loadedSchedule))
                    }
                } else {
                    draftSchedule.days = JSON.parse(JSON.stringify(loadedSchedule.days))
                    this.data.appliedSchedule = JSON.parse(JSON.stringify(this.data.draftSchedule))
                }
            })

            this.$on('local:afterChange:draftSchedule.*.days.*', () => {
                this.hasUnappliedSlots = !this.isScheduleActionsDisabled
            })
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    @include breakpoint(tablet-small) {
        .appointment_length--row {
            margin-top: 20px;
        }
    }

    .availability-schedule-form-icon {
        top: 3.125rem !important;
    }
</style>
